import { Box, Paper, Typography } from '@mui/material';
import { useMemo } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { UserFeedback } from '@aviation/cabinapp-userfeedbackservice-sdk';

export interface IUserFeedbackChartsProps {
    data?: UserFeedback[];
}

// Brand-aligned palette (primary / secondary / info shades from the theme).
const TYPE_COLORS: { [key: string]: string } = {
    Issue: '#092A5E',
    Idea: '#70CBF4',
};
const VERSION_COLORS = ['#092A5E', '#70CBF4', '#1890FF', '#103996', '#74CAFF', '#176599', '#637381', '#C4CDD5'];

interface ChartDatum {
    name: string;
    value: number;
}

function ChartCard({ title, data, colors }: { title: string; data: ChartDatum[]; colors: (entry: ChartDatum, index: number) => string }) {
    return (
        <Paper sx={{ p: 2 }} elevation={3}>
            <Typography variant='subtitle1' component='h2' sx={{ textTransform: 'uppercase', mb: 1 }}>
                {title}
            </Typography>
            {data.length === 0 ? (
                <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant='body2' color='text.secondary'>No data</Typography>
                </Box>
            ) : (
                <ResponsiveContainer width='100%' height={240}>
                    <PieChart>
                        <Pie data={data} dataKey='value' nameKey='name' cx='50%' cy='50%' outerRadius={80}>
                            {data.map((entry, index) => (
                                <Cell key={entry.name} fill={colors(entry, index)} />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            )}
        </Paper>
    );
}

function UserFeedbackCharts({ data = [] }: IUserFeedbackChartsProps) {
    const typeData = useMemo<ChartDatum[]>(() => {
        const counts = data.reduce<{ [key: string]: number }>((acc, item) => {
            const key = item.Type || 'Unknown';
            acc[key] = (acc[key] ?? 0) + 1;
            return acc;
        }, {});
        return Object.entries(counts).map(([name, value]) => ({ name, value }));
    }, [data]);

    const versionData = useMemo<ChartDatum[]>(() => {
        const counts = data.reduce<{ [key: string]: number }>((acc, item) => {
            const key = item.AppVer || 'Unknown';
            acc[key] = (acc[key] ?? 0) + 1;
            return acc;
        }, {});
        return Object.entries(counts)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value);
    }, [data]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <ChartCard
                title='Types'
                data={typeData}
                colors={(entry, index) => TYPE_COLORS[entry.name] ?? VERSION_COLORS[index % VERSION_COLORS.length]}
            />
            <ChartCard
                title='App Versions'
                data={versionData}
                colors={(_, index) => VERSION_COLORS[index % VERSION_COLORS.length]}
            />
        </Box>
    );
}

export default UserFeedbackCharts;
